import React from "react";
import { useTranslation } from "react-i18next";
import { LicensePlan } from "../../../store/actions/PlanActions";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import {
  PiPlusLight,
  PiSparkleLight,
  PiFunnelLight,
  PiFileTextLight,
  PiChartBarLight,
  PiBookOpenLight,
  PiLightningLight,
  PiPuzzlePieceLight,
  PiUsersLight,
} from "react-icons/pi";
import { IconType } from "react-icons";

/** Map add-on sections/names to a contextual icon */
const getAddonIcon = (addon: LicensePlan): { icon: IconType } => {
  const name = (addon.name || addon.display_name || "").toLowerCase();
  const sections = addon.feature_gates?.sections || [];
  const aiFeatures = addon.feature_gates?.ai_features || [];

  if (
    aiFeatures.length > 0 ||
    name.includes("ai") ||
    sections.some((s) => ["research", "tables"].includes(s))
  )
    return { icon: PiSparkleLight };

  if (name.includes("template") || sections.includes("templates"))
    return { icon: PiFileTextLight };

  if (name.includes("pipeline") || sections.includes("client_pipeline"))
    return { icon: PiFunnelLight };

  if (name.includes("analytics") || sections.includes("analytics"))
    return { icon: PiChartBarLight };

  if (name.includes("knowledge") || sections.includes("data_sources"))
    return { icon: PiBookOpenLight };

  return { icon: PiPuzzlePieceLight };
};


interface AddOnCardProps {
  addon: LicensePlan;
  billingPeriod: "monthly" | "annual";
  isActive: boolean;
  onAdd: (addon: LicensePlan) => void;
  onViewDetails: (addon: LicensePlan) => void;
  onManageAssignments?: (addon: LicensePlan) => void;
  isLoading?: boolean;
  teamSize?: number;
  /** For active per-seat addons: how many seats are assigned */
  assignedCount?: number;
  /** For active per-seat addons: total purchased seats */
  totalSeats?: number;
  isOrgAdmin?: boolean;
}

const AddOnCard: React.FC<AddOnCardProps> = ({
  addon,
  billingPeriod,
  isActive,
  onAdd,
  onViewDetails,
  onManageAssignments,
  isLoading,
  teamSize,
  assignedCount,
  totalSeats,
  isOrgAdmin = true,
}) => {
  const { t } = useTranslation();
  const price = billingPeriod === "monthly" ? addon.price_monthly : addon.price_annual;
  const addonIcon = getAddonIcon(addon);
  const AddonIcon = addonIcon.icon;
  const teamDiscount = addon.team_discount_percent || 0;
  const teamPrice = teamDiscount > 0 ? price * (1 - teamDiscount / 100) : price;

  return (
    <div
      className={`
        border rounded-lg overflow-hidden bg-white dark:bg-gray-900
        cursor-pointer transition-colors
        ${isActive
          ? "border-primary-200 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10 ring-1 ring-primary-500"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        }
      `}
      onClick={() => onViewDetails(addon)}
    >
      <div className="p-5 flex flex-col">
        {/* Title row: icon + name + price badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 min-w-0">
            <AddonIcon className="h-4 w-4 text-gray-900 dark:text-gray-100 flex-shrink-0" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
              {addon.display_name}
            </h4>
            {isActive && (
              <Badge color="green" className="text-xs flex-shrink-0">
                {t("Active")}
              </Badge>
            )}
          </div>
          <Badge color="green" className="text-sm font-medium px-3 py-1 flex-shrink-0">
            <span className="text-gray-900 dark:text-gray-100">${price.toFixed(0)}</span>
            <span className="text-xs font-normal">
              /{addon.is_per_seat ? t("seat") + "/" : ""}{t("mo")}
            </span>
            {billingPeriod === "annual" && (
              <span className="text-xs font-normal ml-1 opacity-70">
                {t("billed annually")}
              </span>
            )}
          </Badge>
        </div>

        {/* Team discount note */}
        {!isActive && teamDiscount > 0 && addon.is_per_seat && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-green-600 dark:text-green-400 px-2.5 py-0.5 rounded-full font-medium ring-1 ring-inset ring-gray-300/50 dark:ring-gray-600/50">
              {t("Buy for whole team")} — ${teamPrice.toFixed(0)}/{t("seat")}/{t("mo")}
              <span className="text-gray-900 dark:text-gray-100">
                ({teamDiscount}%)
              </span>
            </span>
          </div>
        )}

        {/* Description + Action buttons on same row */}
        <div className="flex items-end justify-between gap-3 mt-1">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
              {addon.description}
            </p>
            {/* Seat usage for active per-seat addons */}
            {isActive && addon.is_per_seat && totalSeats != null && assignedCount != null && (
              <p className="text-xs text-gray-900 dark:text-gray-100 mt-3 flex items-center gap-1">
                <PiUsersLight className="h-3.5 w-3.5" />
                {assignedCount}/{totalSeats} {t("in use")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isActive ? (
              addon.is_per_seat && onManageAssignments ? (
                <Button
                  variant="outline"
                  size="small"
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); onManageAssignments(addon); }}
                  icon={<PiUsersLight />}
                >
                  {t("Manage")}
                </Button>
              ) : (
                <Button variant="neutral" size="small" disabled>
                  {t("Active")}
                </Button>
              )
            ) : (
              <Button
                variant={isOrgAdmin ? "primary" : "outline"}
                size="small"
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onAdd(addon); }}
                disabled={isLoading}
                isLoading={isLoading}
                icon={isOrgAdmin ? <PiPlusLight /> : undefined}
              >
                {isOrgAdmin ? t("Add") : t("Request")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnCard;
